import React, { useCallback, useEffect, useRef, useState } from "react";
import { Ban, LogOut, Plus, QrCode, Search, Settings2 } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "../components/Layout";
import api, { formatApiError, hasWrite, isApprover } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GatePassForm } from "../components/gatepass/GatePassForm";
import { GatePassView, StatusBadge } from "../components/gatepass/GatePassView";
import { InvoiceBadge, invStatus } from "../components/gatepass/InvoiceMark";
import { GatePassSettings } from "../components/gatepass/GatePassSettings";
import { GatePassReport } from "../components/gatepass/GatePassReport";
import { VehicleTrips } from "../components/gatepass/VehicleTrips";
import { EmployeesOut } from "../components/gatepass/EmployeesOut";

const ctl = "h-10 px-2 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const TYPE = { invoice: "Invoice", uchanti: "Uchanti", company_vehicle: "Company vehicle", foc: "FOC", no_work: "No work done", outside_work: "Outside work", employee: "Employee", trial: "Trial" };
const COLS = "grid grid-cols-2 md:grid-cols-[120px_120px_minmax(0,1fr)_120px_110px_130px_90px] gap-2 md:gap-4 px-4 md:px-5";

// Polls the list and pops a toast when a vehicle is released at the gate.
const useGatePasses = (filters) => {
  const [rows, setRows] = useState([]);
  const prev = useRef({});
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/gatepass", { params: Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) });
      data.forEach((g) => { if (prev.current[g.id] === "issued" && g.status === "out") toast.success(`VEHICLE OUT · ${g.vehicle_number} (${g.number})`, { duration: 10000 }); });
      prev.current = Object.fromEntries(data.map((g) => [g.id, g.status]));
      setRows(data);
    } catch (e) { console.error(e); }
  }, [filters]);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);
  return { rows, load };
};

const GpRow = ({ g, onView, canCancel, onCancel, onMarkOut }) => (
  <div data-testid={`gp-row-${g.id}`} className={`${COLS} py-3.5 items-center text-sm hover:bg-background/50 transition-colors`}>
    <button data-testid={`gp-open-${g.id}`} onClick={() => onView(g)} className="num font-bold text-primary text-left flex items-center gap-1.5"><QrCode size={14} /> {g.number}</button>
    <span className="num font-semibold break-all">{g.vehicle_number}</span>
    <div className="min-w-0 col-span-2 md:col-span-1">
      <div className="font-medium break-words">{g.party_name || g.remarks || "—"}</div>
      <div className="text-xs text-muted-foreground break-words">{TYPE[g.gp_type]}{g.job_card_number ? ` · JC ${g.job_card_number}` : ""}{g.to_location ? ` · → ${g.to_location}` : ""} · {g.driver_name}</div>
    </div>
    <span className="text-xs uppercase font-semibold">{g.payment_type === "none" ? "—" : g.payment_type}<span className="block mt-0.5 normal-case"><InvoiceBadge g={g} /></span></span>
    <span className="num text-xs">{g.date}<span data-testid={`gp-issuer-${g.id}`} className="block text-[11px] text-muted-foreground font-sans">By {g.created_by_name || "—"}</span></span>
    <StatusBadge g={g} />
    <div className="flex items-center justify-end gap-3">
      {g.status === "out" && invStatus(g) === "pending" && <button data-testid={`gp-markinv-${g.id}`} onClick={() => onView(g)} className="text-[11px] font-semibold px-2 py-1 rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors">Mark invoice</button>}
      {canCancel && g.status === "issued" && <button data-testid={`gp-markout-${g.id}`} onClick={() => onMarkOut(g)} title="Mark vehicle out by hand" className="text-muted-foreground hover:text-[hsl(var(--positive))]"><LogOut size={15} /></button>}
      {canCancel && g.status === "issued" && <button data-testid={`gp-cancel-${g.id}`} onClick={() => onCancel(g)} title="Cancel gate pass" className="text-muted-foreground hover:text-destructive"><Ban size={15} /></button>}
    </div>
  </div>
);

export default function GatePass() {
  const { user } = useAuth();
  const canWrite = hasWrite(user);
  const admin = isApprover(user);
  const [filters, setFilters] = useState({ status: "", start: "", end: "", search: "" });
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [tab, setTab] = useState("list");
  const { rows, load } = useGatePasses(filters);
  const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  const cancel = async (g) => {
    if (!window.confirm(`Cancel gate pass ${g.number} for ${g.vehicle_number}?`)) return;
    try { await api.post(`/gatepass/${g.id}/cancel`); toast.success("Gate pass cancelled"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markOut = async (g) => {
    const reason = window.prompt(`Mark ${g.vehicle_number} (${g.number}) as gone out by hand?\n\nReason (required), e.g. Hub scanner not working:`, "");
    if (reason === null) return;
    if (!reason.trim()) return toast.error("A reason is required");
    try { await api.post(`/gatepass/${g.id}/mark-out`, { reason }); toast.success(`VEHICLE OUT · ${g.vehicle_number}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
        <PageHeader title="Gate Pass" subtitle="Issue a QR gate pass; the guard scans it in Auto Inventory Hub to let the vehicle out.">
          <div className="flex gap-2">
            {admin && <button data-testid="gp-settings-btn" onClick={() => setShowSettings((s) => !s)} className="h-10 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors"><Settings2 size={15} /> Settings</button>}
            {canWrite && <button data-testid="gp-new-btn" onClick={() => setCreating(true)} className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors"><Plus size={15} /> New gate pass</button>}
          </div>
        </PageHeader>
        {admin && showSettings && <GatePassSettings />}
        <div className="inline-flex flex-wrap rounded-lg border border-border bg-card p-1 mb-5">
          {[["list", "Gate passes"], ["report", "Daily report"], ["trips", "Vehicle trips"], ["staff", "Employees out"]].map(([k, l]) => (
            <button key={k} data-testid={`gp-tab-${k}`} onClick={() => setTab(k)} className={`px-4 h-9 rounded-md text-sm font-medium transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
        {tab === "report" ? <GatePassReport /> : tab === "trips" ? <VehicleTrips /> : tab === "staff" ? <EmployeesOut /> : (<>
        <div className="flex flex-wrap items-end gap-3 mb-5">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="gp-search" className={`${ctl} w-full pl-9`} placeholder="Search number, vehicle, party, driver, job card…" value={filters.search} onChange={setF("search")} />
          </div>
          <select data-testid="gp-status-filter" className={ctl} value={filters.status} onChange={setF("status")}>
            <option value="">All</option><option value="issued">Inside (not out)</option><option value="out">Vehicle out</option><option value="cancelled">Cancelled</option>
          </select>
          <input data-testid="gp-start" type="date" className={`${ctl} num`} value={filters.start} onChange={setF("start")} />
          <input data-testid="gp-end" type="date" className={`${ctl} num`} value={filters.end} onChange={setF("end")} />
        </div>
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <div className="md:min-w-[900px]">
            <div className={`${COLS} hidden md:grid py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground`}>
              <span>Gate pass</span><span>Vehicle</span><span>Account / details</span><span>Payment</span><span>Date</span><span>Status</span><span></span>
            </div>
            {rows.length === 0 ? <div className="p-10 text-center text-sm text-muted-foreground">No gate passes yet.</div> : (
              <div className="divide-y divide-border">{rows.map((g) => <GpRow key={g.id} g={g} onView={setView} canCancel={admin} onCancel={cancel} onMarkOut={markOut} />)}</div>
            )}
          </div>
        </div>
        </>)}
        <GatePassForm open={creating} onClose={() => setCreating(false)} onCreated={(g) => { setCreating(false); load(); setView(g); }} />
        <GatePassView g={view} onClose={() => setView(null)} onUpdated={(d) => { setView(d); load(); }} />
      </div>
    </Layout>
  );
}
